import classNames from 'classnames'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { Button } from '../../components/design-system/button'
import { Grid, Icon, PrioritySupport, UnlimitedProjects } from './icons'
import { IBlockedViewInteractor } from './interactor'

const animationClassName =
  'transition-all duration-300 ease-[cubic-bezier(0.5,0.35,0.15,1)] supports-[-webkit-appearance:none]:transition-none'

const Logo: React.FC = () => (
  <svg
    width="115"
    height="32"
    viewBox="0 0 115 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="8" fill="#374FD5" />
    <path
      d="M21.3125 15.7188L13.9062 22.3125V10.125L11.2812 11.5003V24.7188L12.9375 26.0625L25.125 15.125L12.75 5.3125L10.125 6.6875L21.3125 15.7188Z"
      fill="white"
    />
    <path
      d="M44.4352 24.5H48.1617L52.3688 10.8945H53.4469V7.58984H50.3414L44.4352 24.5ZM47.6109 20.3984H57.2203L56.3531 17.75H48.4781L47.6109 20.3984ZM56.6813 24.5H60.3961L54.5016 7.58984H52.4742V10.8945L56.6813 24.5ZM62.265 24.5H65.6869V17.2344C65.6869 16.6797 65.7884 16.1953 65.9916 15.7812C66.2025 15.3594 66.4955 15.0312 66.8705 14.7969C67.2455 14.5625 67.6869 14.4453 68.1947 14.4453C68.9759 14.4453 69.558 14.6758 69.9408 15.1367C70.3236 15.5898 70.515 16.2344 70.515 17.0703V24.5H73.9369V16.3438C73.9369 14.8594 73.558 13.6992 72.8002 12.8633C72.0502 12.0273 70.9798 11.6094 69.5892 11.6094C68.6517 11.6094 67.8627 11.8164 67.222 12.2305C66.5814 12.6445 66.1009 13.2109 65.7806 13.9297H65.6869V11.8789H62.265V24.5ZM76.6261 24.5H80.048V11.8789H76.6261V24.5ZM78.337 10.2031C78.8605 10.2031 79.298 10.0273 79.6495 9.67578C80.0089 9.31641 80.1886 8.89062 80.1886 8.39844C80.1886 7.89844 80.0089 7.47266 79.6495 7.12109C79.298 6.76953 78.8605 6.59375 78.337 6.59375C77.8214 6.59375 77.3839 6.76953 77.0245 7.12109C76.673 7.47266 76.4972 7.89844 76.4972 8.39844C76.4972 8.89062 76.673 9.31641 77.0245 9.67578C77.3839 10.0273 77.8214 10.2031 78.337 10.2031ZM82.7841 24.5H86.2059V17.2344C86.2059 16.6797 86.3075 16.1953 86.5106 15.7812C86.7216 15.3594 87.0145 15.0312 87.3895 14.7969C87.7645 14.5625 88.2059 14.4453 88.7138 14.4453C89.495 14.4453 90.077 14.6758 90.4598 15.1367C90.8427 15.5898 91.0341 16.2344 91.0341 17.0703V24.5H94.4559V16.3438C94.4559 14.8594 94.077 13.6992 93.3192 12.8633C92.5692 12.0273 91.4989 11.6094 90.1083 11.6094C89.1708 11.6094 88.3817 11.8164 87.7411 12.2305C87.1005 12.6445 86.62 13.2109 86.2997 13.9297H86.2059V11.8789H82.7841V24.5ZM97.1452 24.5H100.567V11.8789H97.1452V24.5ZM98.8561 10.2031C99.3795 10.2031 99.817 10.0273 100.169 9.67578C100.528 9.31641 100.708 8.89062 100.708 8.39844C100.708 7.89844 100.528 7.47266 100.169 7.12109C99.817 6.76953 99.3795 6.59375 98.8561 6.59375C98.3405 6.59375 97.903 6.76953 97.5436 7.12109C97.192 7.47266 97.0163 7.89844 97.0163 8.39844C97.0163 8.89062 97.192 9.31641 97.5436 9.67578C97.903 10.0273 98.3405 10.2031 98.8561 10.2031ZM102.424 24.5H106.034L108.401 20.1641H108.495L110.885 24.5H114.623L110.686 18.125L114.635 11.8789H110.92L108.67 16.1328H108.577L106.315 11.8789H102.448L106.385 18.2188L102.424 24.5Z"
      fill="#0B1118"
    />
  </svg>
)

const Heading: React.FC<{ className: string }> = ({ className }) => (
  <div className={classNames('text-center', className)}>
    <h1 className="text-5xl font-semibold">Experience the might of PRO</h1>
    <h2 className="mt-4 text-xl">
      Free accounts can only have 2 active projects on the Aninix platform.
      <br />
      Please upgrade to PRO or delete an existing project.
    </h2>
  </div>
)

const Feature: React.FC<{
  Icon: Icon
  title: React.ReactNode
  comment: React.ReactNode
  isFirst?: boolean
  isLast?: boolean
}> = ({ Icon, title, comment, isFirst, isLast }) => (
  <div className="relative">
    <div
      className={classNames(
        'absolute left-0 top-0 h-full w-full opacity-0',
        animationClassName
      )}
    >
      {/* reflect right */}
      <div
        className={classNames(
          'absolute left-0 top-1/2 h-[80%] w-[30px] -translate-x-full -translate-y-1/2 rounded-br-2xl rounded-tr-2xl bg-gradient-to-l from-[#D7E6FA] to-[#D7E6FA00]',
          { '!opacity-0': isFirst }
        )}
      />
      {/* reflect left */}
      <div
        className={classNames(
          'absolute right-0 top-1/2 h-[80%] w-[30px] -translate-y-1/2 translate-x-full rounded-bl-2xl rounded-tl-2xl bg-gradient-to-r from-[#D7E6FA] to-[#D7E6FA00]',
          { '!opacity-0': isLast }
        )}
      />
    </div>

    <div className="relative flex h-[300px] w-[220px] flex-shrink-0 gap-6 overflow-hidden rounded-2xl p-4 shadow-lg shadow-[#5A87BF4D]">
      <div
        className={classNames(
          'absolute z-10 h-full w-full',
          animationClassName
        )}
      >
        {/* circle right */}
        <div className="absolute right-0 top-0 h-[320px] w-[320px] -translate-y-1/2 translate-x-1/2 rounded-full bg-[#D5F5CC]/50 blur-3xl" />
        {/* circle left */}
        <div className="absolute bottom-0 left-0 h-[320px] w-[320px] -translate-x-1/2 translate-y-1/2 rounded-full bg-[#D7E6FA]/50 blur-3xl" />
        <Grid className="absolute left-1/2 top-0 w-full -translate-x-1/2" />
      </div>

      <div className="relative z-20 flex h-full w-full flex-col items-center justify-center gap-6">
        <Icon isHovered className={animationClassName} />
        <div className="text-center">
          <p className="text-lg font-semibold">{title}</p>
          <p
            className={classNames(
              'mt-2 text-base text-[#788FAC]',
              animationClassName
            )}
          >
            {comment}
          </p>
        </div>
      </div>
    </div>
  </div>
)

const Features: React.FC<{ className?: string }> = ({ className }) => (
  <div className={classNames('flex flex-row justify-center gap-6', className)}>
    <Feature
      Icon={UnlimitedProjects}
      title={
        <>
          Unlimited
          <br />
          projects
        </>
      }
      comment={
        <>
          We’re curious if you can
          <br />
          top up your files!
        </>
      }
    />
    <Feature
      Icon={PrioritySupport}
      title={
        <>
          Priority
          <br />
          support
        </>
      }
      comment={
        <>
          We care about
          <br />
          each of you
        </>
      }
      isLast
    />
  </div>
)

const UpgradeToPro: React.FC<{
  interactor: IBlockedViewInteractor
  className?: string
}> = ({ interactor, className }) => {
  return (
    <div className={classNames('flex flex-col items-center gap-6', className)}>
      <div className="flex flex-row items-center gap-6">
        <div className="flex w-36 flex-col gap-1 text-center">
          <p className="text-5xl font-semibold">${interactor.monthlyPrice}</p>
          <p className="text-base">per month</p>
        </div>

        <button
          className={classNames(
            'flex-1 rounded-xl border-[1px] border-[#5A87BF26] bg-gradient-to-b from-[#5A87BF26] to-[#5A87BF26] p-3 text-left disabled:opacity-60',
            animationClassName,
            {
              '!border-transparent !from-[#D5F5CC4D] !to-[#D7E6FA4D] shadow-lg shadow-[#5A87BF4D]':
                interactor.isAnnual,
            }
          )}
          onClick={interactor.toggleAnnual}
          disabled={interactor.state !== 'not-started'}
        >
          <div className="flex flex-row items-center">
            <div
              className={classNames(
                'relative h-[20px] w-[36px] rounded-full bg-gradient-to-b from-[#5A87BFCC] to-[#5A87BF66]',
                { '!from-[#169C64] !to-[#1BC47D]': interactor.isAnnual }
              )}
            >
              <span
                className={classNames(
                  'absolute left-0 top-[2px] block h-[16px] w-[16px] translate-x-[2px] rounded-full bg-white shadow-sm',
                  animationClassName,
                  { '!translate-x-[calc(100%+2px)]': interactor.isAnnual }
                )}
              />
            </div>
            <p className="ml-2 mr-4 flex-grow">Annual subscription</p>
            <p
              className={classNames(
                'rounded-lg bg-white px-2 py-[2px] font-semibold',
                animationClassName,
                { '!bg-[#1BC47D] !text-white': interactor.isAnnual }
              )}
            >
              Save ${interactor.savings}
            </p>
          </div>

          <p className="mt-1">
            <span className="font-medium">
              ${interactor.annualPrice} billed annually
            </span>{' '}
            as a recurrent payment
          </p>
        </button>
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button
          label="Cancel"
          onClick={interactor.cancel}
          buttonType="button"
          className={classNames(
            '!border-[1px] !border-solid !border-gray-700 !bg-transparent !text-gray-700',
            { '!hidden': interactor.state !== 'pending' }
          )}
        />

        <Button
          label="Upgrade to PRO"
          onClick={interactor.upgradeToPro}
          buttonType="button"
          className={classNames('!bg-[#1BC47D] !text-lg !font-semibold', {
            hidden: interactor.state === 'pending',
          })}
        />

        {interactor.state === 'pending' && (
          <p className="text-center">
            Payment pending. Complete all steps via Stripe.
            <br />
            If you have any questions, please contact us at{' '}
            <a href="mailto:info@aninix.com" className="text-accent">
              info@aninix.com
            </a>
            .
          </p>
        )}

        {interactor.error && <p>{interactor.error}</p>}

        {interactor.state === 'not-started' && (
          <Link
            to="https://www.aninix.com/pricing"
            className={classNames(
              'text-xs text-gray-400 hover:text-accent',
              animationClassName
            )}
            target="_blank"
          >
            Learn more about pricing
          </Link>
        )}
      </div>
    </div>
  )
}

const Links: React.FC<{
  className?: string
  interactor: IBlockedViewInteractor
}> = ({ className, interactor }) => {
  const linkClasses = 'text-gray-400 hover:text-accent'

  return (
    <div
      className={classNames(
        'flex flex-row items-center gap-2 text-sm',
        className
      )}
    >
      <Link
        to={interactor.existingProjectsUrl}
        className={classNames(linkClasses, animationClassName)}
      >
        Manage existing projects
      </Link>
      <p className="text-[10px] text-gray-300">•</p>
      <Link
        to="https://www.aninix.com/privacy-policy"
        className={classNames(linkClasses, animationClassName)}
        target="_blank"
      >
        Privacy policy
      </Link>
      <p className="text-[10px] text-gray-300">•</p>
      <Link
        to="https://www.aninix.com/terms-of-conditions"
        className={classNames(linkClasses, animationClassName)}
        target="_blank"
      >
        Terms and conditions
      </Link>
    </div>
  )
}

export interface IProps {
  interactor: IBlockedViewInteractor
}
export const PaymentRequiredViewRouter: React.FC<IProps> = ({ interactor }) => (
  <div className="mx-auto flex min-h-screen w-screen max-w-5xl flex-col items-center justify-start p-6 pb-16">
    <div className="flex w-full flex-row justify-between">
      <a href="/">
        <Logo />
      </a>

      <div className="flex flex-row items-center gap-3">
        <p className="text-right txt-[18_26_500]">
          {interactor.account.name}
          <span className="block text-[#89909A] txt-[11_14_400]">
            {interactor.account.email}
          </span>
        </p>

        <img
          crossOrigin="anonymous"
          className="h-8 w-8 rounded-full object-fill"
          src={interactor.account.avatarUrl}
        />
      </div>
    </div>
    <Heading className="mt-20" />
    <UpgradeToPro className="mt-8" interactor={interactor} />
    <Features className="mt-16" />
    <Links className="mt-16" interactor={interactor} />
  </div>
)
